import _faqEntries from '@general_inquiry/faq-content'
import {ActionType, FeatureStatus} from '@utils/feature_tracking';
import FeatureTrackingBuilder from '@utils/feature_tracking_builder';
import * as tracking from '@utils/tracking'

class FaqModule {

    private static readonly TOP_PARENT_FEATURE_ID = 'contactflow_faq_general'
    private static readonly TOP_PARENT_FEATURE_NAME = 'ContactflowFAQModulGeneral'
    private static readonly QUESTION_FEATURE_ID = 'contactflow_gen_question'
    private static readonly QUESTION_FEATURE_NAME = 'ContactflowGenQuestion'
    private static readonly QUESTION_LINK_FEATURE_NAME = 'ContactflowQuestionLink';

    private readonly _documentObject: Document
    private readonly updateClickBindings: () => void
    private readonly $faqQuestionsStep: HTMLDivElement | null
    private readonly $faqAnswersStep: HTMLDivElement | null
    private readonly $questionsArea: Element | undefined
    private readonly $questionBlueprint: Element | undefined
    private readonly $answersHeadline: HTMLHeadingElement | undefined
    private readonly $answersContentArea: HTMLDivElement | undefined
    private readonly $answersLoader: HTMLDivElement | undefined
    private readonly $topicItems: NodeListOf<HTMLDivElement> | undefined
    private readonly faqEntries: FaqEntry[] | undefined
    private readonly trackingBaseLabels = {
        ot_PageCluster: 'Kontaktformulare',
    }
    private readonly _windowObject: Window
    private $questionHeadline: HTMLSpanElement | undefined
    private $questionIcon: Element | undefined

    private constructor(windowObject: Window, documentObject: Document, topicItems: NodeListOf<HTMLDivElement>, updateClickBindings: () => void) {
        this._windowObject = windowObject
        this._documentObject = documentObject
        this.updateClickBindings = updateClickBindings
        this.$faqQuestionsStep = this._documentObject.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep"]')
        this.$faqAnswersStep = this._documentObject.querySelector('[data-js-hook="con_generalinquiry-faqanswersstep"]')
        if (this.$faqQuestionsStep && this.$faqAnswersStep) {
            this.faqEntries = this.parseFaqEntries()
            this.$topicItems = topicItems
            this.$questionsArea = this.$faqQuestionsStep.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__question-area"]')!
            this.$questionBlueprint = this.$faqQuestionsStep.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__question-blueprint"]')!
            this.$questionHeadline = this.$faqQuestionsStep.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__question-headline"]')! as HTMLSpanElement
            this.$questionIcon = this.$faqQuestionsStep.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__question-icon-href"]')!
            this.$answersHeadline = this._documentObject.querySelector('[data-js-hook="con_focussed-dialog-title__faqanswersstep"]')! as HTMLHeadingElement
            this.$answersContentArea = this.$faqAnswersStep.querySelector('[data-js-hook="con_generalinquiry-faqanswersstep__answer-area"]')! as HTMLDivElement
            this.$answersLoader = this.$faqAnswersStep.querySelector('[data-js-hook="con_generalinquiry-faqanswersstep__answer-loader"]')! as HTMLDivElement
            this.bindFaqQuestions()
        }
    }

    private parseFaqEntries() {
        const faqEntriesFromJson = _faqEntries as FaqEntry[]
        faqEntriesFromJson.forEach((entry) => {
            entry.questions.forEach((question, index) => {
                question.index = index + 1
            })
        })
        return faqEntriesFromJson
    }

    private bindFaqQuestions() {
        Array.from(this.$topicItems!).forEach(
            (topicItem) => topicItem.addEventListener('click', () => {
                this.displayQuestionsForTopic(topicItem)
            })
        )
    }

    private displayQuestionsForTopic(topic: HTMLDivElement) {
        const faqEntry = this.faqEntries!.find(entry => entry.topic === topic.dataset.jsSubjectValue!)
        if (faqEntry) {
            this.clearPreviousQuestions()
            this.renderQuestionHeadline(topic)
            this.renderQuestions(faqEntry)
            this.sendShowTrackingForQuestions(faqEntry.questions)
        }
    }

    private clearPreviousQuestions() {
        /*                                                                              */
        this.$questionsArea?.replaceChildren(this.$questionsArea?.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__question"]')!)
    }

    private renderQuestionHeadline(topic: HTMLDivElement) {
        const topicIcon = topic.querySelector('[data-js-hook="con_generalinquiry-topicchoicestep__topic-icon-type"]')!.getAttribute('icon-left')!
        this.$questionIcon?.setAttribute('type', topicIcon)
        this.$questionHeadline!.innerText = topic.dataset.jsSubjectText!
    }

    private renderQuestions(faqEntry: FaqEntry) {
        if (this.$questionBlueprint) {
            faqEntry.questions.forEach(question => {
                const newQuestion = this.$questionBlueprint!.cloneNode(true) as HTMLElement
                newQuestion.style.removeProperty('display')
                newQuestion.dataset.jsHook = 'con_generalinquiry-faqquestionsstep__question-item'
                newQuestion.querySelector('[data-js-hook="con_generalinquiry-faqquestionsstep__question-text"]')!.textContent = question.question
                newQuestion.addEventListener('click', () => this.displayAnswer(question, faqEntry.type))
                this.$questionsArea?.appendChild(newQuestion)
            })
            this.updateClickBindings()
        }
    }

    private displayAnswer(faqQuestion: FaqQuestion) {
        this._windowObject.scrollTo(0, 0)
        this.renderAnswerHeadline(faqQuestion.question)
        this.renderAnswerContent(faqQuestion)
    }

    private renderAnswerHeadline(question: string) {
        this.$answersHeadline!.innerText = question
    }

    private renderAnswerContent(faqQuestion: FaqQuestion) {
        this.$answersContentArea!.innerHTML = '';
        this.$answersLoader!.classList.remove('con_generalinquiry-faqanswersstep__answer-loader__answer-area-loaded')
        const newOrderListResponse: Promise<AnswerQueryResponse> = this.fetchAnswer(faqQuestion.answerUrl)
        newOrderListResponse.then(
            (response) => {
                this.$answersLoader!.classList.add('con_generalinquiry-faqanswersstep__answer-loader__answer-area-loaded')
                this.$answersContentArea!.innerHTML = response.answerContent
                this.emitFocussedDialogInitEvent(this.$answersContentArea!)
                this.registerQuestionClickTracking(faqQuestion)
            },
            (error) => {
                console.log(error)
                this.registerQuestionClickErrorTracking(faqQuestion)
            }
        )
    }

    private async fetchAnswer(answerUrl: string): Promise<AnswerQueryResponse> {
        const response = await fetch(answerUrl)
        if (response.ok) {
            const returnedAnswerHtml = await response.text()
            return new AnswerQueryResponse(returnedAnswerHtml)
        } else {
            return Promise.reject(new AnswerQueryResponse(''))
        }
    }

    private emitFocussedDialogInitEvent(target: HTMLDivElement) {
        const globalWindow = this._windowObject as unknown as Window & {
            o_global: {
                eventQBus: {
                    emit: (...args: any[]) => void
                }
            }
        }

        globalWindow.o_global.eventQBus.emit(
            'assets.focusseddialog.init',
            this._windowObject.location.search,
            this._windowObject.location.href,
            target
        )
    }

    private sendShowTrackingForQuestions(questions: FaqQuestion[]) {
        const parentFeature = this.createParentFeature(FeatureStatus.LOADED)
        const questionFeatures: any[] = []

        questions.forEach(question => {
            const features = this.createQuestionFeature(FaqModule.QUESTION_FEATURE_ID + question.index, question.trackingLabel, FeatureStatus.COLLAPSED)
            questionFeatures.push(...[features])
        })

        const featureTree = {
            name: ActionType.AUTOMATIC,
            features: [parentFeature, ...questionFeatures]
        }
        tracking.sendEventWithActionToTrackingServer(this.trackingBaseLabels, featureTree)
    }

    private registerQuestionClickTracking(faqQuestion: FaqQuestion) {
        const parentFeature = this.createParentFeature(FeatureStatus.CLICKED)
        const questionFeature = this.createQuestionFeature(FaqModule.QUESTION_FEATURE_ID + faqQuestion.index, faqQuestion.trackingLabel, FeatureStatus.EXPANDED)
        const featureTree = {
            name: ActionType.CLICK,
            features: [parentFeature, questionFeature]
        }
        tracking.sendEventWithActionToTrackingServer(this.trackingBaseLabels, featureTree)
    }

    private registerQuestionClickErrorTracking(faqQuestion: FaqQuestion) {
        const trackingBaseLabels = {
            ...this.trackingBaseLabels,
            contact_FaqError: 'no_content'
        }
        const parentFeature = this.createParentFeature(FeatureStatus.CLICKED)
        const questionFeature = this.createQuestionFeature(FaqModule.QUESTION_FEATURE_ID + faqQuestion.index, faqQuestion.trackingLabel, FeatureStatus.EXPANDED)
        const featureTree = {
            name: ActionType.CLICK,
            features: [parentFeature, questionFeature]
        }
        tracking.sendEventWithActionToTrackingServer(trackingBaseLabels, featureTree)
    }

    private createParentFeature(featureStatus: FeatureStatus): object {
        return new FeatureTrackingBuilder(FaqModule.TOP_PARENT_FEATURE_ID, FaqModule.TOP_PARENT_FEATURE_NAME, featureStatus)
            .build()
            .generateFeatureTrackingPayload()
    }

    private createQuestionFeature(featureId: string, questionTrackingLabel: string, featureStatus: FeatureStatus) {
        return new FeatureTrackingBuilder(featureId, FaqModule.QUESTION_FEATURE_NAME, featureStatus)
            .parentId(FaqModule.TOP_PARENT_FEATURE_ID)
            .labels({contact_FaqGenQuestion: [questionTrackingLabel]})
            .build()
            .generateFeatureTrackingPayload()
    }

    static initialize(windowObject: Window, documentObject: Document, topicItems: NodeListOf<HTMLDivElement>, updateClickBindings: () => void): FaqModule {
        return new FaqModule(windowObject, documentObject, topicItems, updateClickBindings)
    }
}

export default FaqModule

type ContactType = 'partner' | 'payment' | 'general'

interface FaqQuestion {
    readonly question: string
    readonly trackingLabel: string
    readonly answerUrl: string
    index?: number
}

interface FaqEntry {
    readonly topic: string
    readonly questions: FaqQuestion[]
    readonly type: ContactType
}

class AnswerQueryResponse {
    private readonly _answerContent: string

    public constructor(answerContent: string) {
        this._answerContent = answerContent
    }

    public get answerContent() {
        return this._answerContent
    }
}
